import { Button } from "@package/subframe/components/Button";
import { IconWithLogoBackground } from "@package/subframe/components/IconWithLogoBackground";
import { LinkButton } from "@package/subframe/components/LinkButton";
import { FeatherArrowLeft, FeatherShieldOff } from "@subframe/core";
import { Link, useSearch } from "@tanstack/react-router";

import { useAuth } from "@/contexts/AuthContext";
import { signOut } from "@/lib/auth";

export default function NoAccessPage() {
  const { restricted } = useSearch({ from: "/no-access" });
  const {
    isAuthenticated,
    memberships,
    nurseryAccess,
    isPlatformAdmin,
    displayLogin,
  } = useAuth();

  const handleSignOut = async () => {
    await signOut();
    window.location.href = "/login";
  };

  // If not logged in, redirect to login
  if (!isAuthenticated) {
    window.location.href = "/login";
    return null;
  }

  // Check if user actually has access - if so, redirect to dashboard (unless restricted)
  const hasActiveMembership = memberships.some((m) => m.status === "ACTIVE");
  const hasNurseryAccess = nurseryAccess.length > 0;

  // Only redirect if not a restricted access case
  if (
    !restricted &&
    (isPlatformAdmin || hasActiveMembership || hasNurseryAccess)
  ) {
    window.location.href = "/dashboard";
    return null;
  }

  // Restricted access - user has nursery access but tried to access admin pages
  if (restricted) {
    return (
      <div className="flex min-h-screen w-full items-center justify-center bg-default-background p-4">
        <div className="flex w-full max-w-[384px] flex-col items-center gap-6">
          {/* Icon */}
          <IconWithLogoBackground
            variant="error"
            size="x-large"
            icon={<FeatherShieldOff />}
          />

          {/* Header */}
          <div className="flex w-full flex-col items-center gap-2">
            <span className="text-heading-1 font-heading-1 text-default-font">
              Restricted area
            </span>
            <span className="text-body font-body text-subtext-color text-center">
              You don&apos;t have permission to access this page. This area is
              restricted to administrators and managers only.
            </span>
          </div>

          {/* Actions */}
          <div className="flex w-full flex-col items-center gap-4">
            <Link to="/dashboard" className="w-full">
              <Button
                className="h-12 w-full"
                variant="brand-primary"
                size="large"
              >
                Go to Dashboard
              </Button>
            </Link>
          </div>

          {/* Signed in info */}
          <span className="text-caption font-caption text-subtext-color">
            Signed in as {displayLogin}
          </span>
        </div>
      </div>
    );
  }

  // No access at all
  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-default-background p-4">
      <div className="flex w-full max-w-[384px] flex-col items-center gap-6">
        {/* Icon */}
        <IconWithLogoBackground
          variant="error"
          size="x-large"
          icon={<FeatherShieldOff />}
        />

        {/* Header */}
        <div className="flex w-full flex-col items-center gap-2">
          <span className="text-heading-1 font-heading-1 text-default-font">
            No access
          </span>
          <span className="text-body font-body text-subtext-color text-center">
            Your account doesn&apos;t have access to any nursery groups or
            nurseries yet. If you believe this is an error, please contact your
            nursery group administrator to request access.
          </span>
        </div>

        {/* Actions */}
        <div className="flex w-full flex-col items-center gap-4">
          <Button
            className="h-12 w-full"
            variant="neutral-primary"
            size="large"
            onClick={handleSignOut}
          >
            Sign out
          </Button>
          <Link to="/login">
            <LinkButton
              variant="neutral"
              size="medium"
              icon={<FeatherArrowLeft />}
            >
              Back to Sign in
            </LinkButton>
          </Link>
        </div>

        {/* Signed in info */}
        <span className="text-caption font-caption text-subtext-color">
          Signed in as {displayLogin}
        </span>
      </div>
    </div>
  );
}
